import React, { useState } from 'react';
import { Car } from 'lucide-react';
import { SEO } from '../components/SEO';

interface TireSize {
  width: string;
  aspectRatio: string;
  diameter: string;
}

interface TireSizeResult {
  sidewallHeight: number;
  overallDiameter: number;
  circumference: number;
  revPerKm: number;
}

function KalkulatorDimenzijeGuma() {
  const [currentSize, setCurrentSize] = useState<TireSize>({
    width: '',
    aspectRatio: '',
    diameter: ''
  });

  const [alternateSize, setAlternateSize] = useState<TireSize>({
    width: '',
    aspectRatio: '',
    diameter: ''
  });

  const [results, setResults] = useState<{
    current: TireSizeResult | null;
    alternate: TireSizeResult | null;
    difference: {
      diameter: number;
      circumference: number;
      speedometer: number;
    } | null;
  }>({
    current: null,
    alternate: null,
    difference: null
  });

  const calculateTireMetrics = (size: TireSize): TireSizeResult | null => {
    const width = parseFloat(size.width);
    const ratio = parseFloat(size.aspectRatio);
    const diameter = parseFloat(size.diameter);

    if (isNaN(width) || isNaN(ratio) || isNaN(diameter)) {
      return null;
    }

    // Convert inches to mm for diameter
    const diameterMm = diameter * 25.4;
    
    // Calculate sidewall height (mm)
    const sidewallHeight = (width * (ratio / 100));
    
    // Calculate overall diameter (mm)
    const overallDiameter = diameterMm + (2 * sidewallHeight);
    
    // Calculate circumference (mm)
    const circumference = Math.PI * overallDiameter;
    
    // Calculate revolutions per kilometer
    const revPerKm = (1000 * 1000) / circumference;

    return {
      sidewallHeight,
      overallDiameter,
      circumference,
      revPerKm
    };
  };

  const calculateDifference = () => {
    const current = calculateTireMetrics(currentSize);
    const alternate = calculateTireMetrics(alternateSize);

    if (!current || !alternate) return;

    const diameterDiff = ((alternate.overallDiameter - current.overallDiameter) / current.overallDiameter) * 100;
    const circumferenceDiff = ((alternate.circumference - current.circumference) / current.circumference) * 100;
    const speedometerDiff = ((current.revPerKm - alternate.revPerKm) / current.revPerKm) * 100;

    setResults({
      current,
      alternate,
      difference: {
        diameter: parseFloat(diameterDiff.toFixed(2)),
        circumference: parseFloat(circumferenceDiff.toFixed(2)),
        speedometer: parseFloat(speedometerDiff.toFixed(2))
      }
    });
  };

  const handleSizeChange = (
    field: keyof TireSize,
    value: string,
    type: 'current' | 'alternate'
  ) => {
    if (type === 'current') {
      setCurrentSize(prev => ({ ...prev, [field]: value }));
    } else {
      setAlternateSize(prev => ({ ...prev, [field]: value }));
    }
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(num);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Dimenzije Guma",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte i usporedite dimenzije guma. Saznajte razlike u promjeru, opsegu i utjecaj na brzinomjer.",
    "url": "https://online-kalkulator.com/kalkulator-dimenzije-guma",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Dimenzije Guma | Usporedba Veličina Guma"
        description="Izračunajte i usporedite dimenzije guma. Saznajte razlike u promjeru, opsegu i utjecaj na brzinomjer. Besplatni online kalkulator."
        canonicalUrl="/kalkulator-dimenzije-guma"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Car className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Dimenzije Guma</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Current Tire Size */}
              <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-3">Trenutna dimenzija gume</h2>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Širina (mm)
                    </label>
                    <input
                      type="number"
                      value={currentSize.width}
                      onChange={(e) => handleSizeChange('width', e.target.value, 'current')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 205"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Profil (%)
                    </label>
                    <input
                      type="number"
                      value={currentSize.aspectRatio}
                      onChange={(e) => handleSizeChange('aspectRatio', e.target.value, 'current')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 55"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Promjer (")
                    </label>
                    <input
                      type="number"
                      value={currentSize.diameter}
                      onChange={(e) => handleSizeChange('diameter', e.target.value, 'current')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 16"
                    />
                  </div>
                </div>
              </div>

              {/* Alternate Tire Size */}
              <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-3">Nova dimenzija gume</h2>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Širina (mm)
                    </label>
                    <input
                      type="number"
                      value={alternateSize.width}
                      onChange={(e) => handleSizeChange('width', e.target.value, 'alternate')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 215"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Profil (%)
                    </label>
                    <input
                      type="number"
                      value={alternateSize.aspectRatio}
                      onChange={(e) => handleSizeChange('aspectRatio', e.target.value, 'alternate')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Promjer (")
                    </label>
                    <input
                      type="number"
                      value={alternateSize.diameter}
                      onChange={(e) => handleSizeChange('diameter', e.target.value, 'alternate')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 17"
                    />
                  </div>
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculateDifference}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Usporedi dimenzije
              </button>

              {/* Results */}
              {results.current && results.alternate && results.difference && (
                <div className="space-y-6 border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-medium text-gray-700 mb-2">Trenutna guma</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Visina boka:</span>
                          <span>{formatNumber(results.current.sidewallHeight)} mm</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Ukupni promjer:</span>
                          <span>{formatNumber(results.current.overallDiameter)} mm</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Opseg:</span>
                          <span>{formatNumber(results.current.circumference)} mm</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Okretaji/km:</span>
                          <span>{formatNumber(results.current.revPerKm)}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-700 mb-2">Nova guma</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Visina boka:</span>
                          <span>{formatNumber(results.alternate.sidewallHeight)} mm</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Ukupni promjer:</span>
                          <span>{formatNumber(results.alternate.overallDiameter)} mm</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Opseg:</span>
                          <span>{formatNumber(results.alternate.circumference)} mm</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Okretaji/km:</span>
                          <span>{formatNumber(results.alternate.revPerKm)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="font-medium text-gray-700 mb-3">Razlike</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Promjer:</span>
                        <span className={`font-semibold ${results.difference.diameter > 2 || results.difference.diameter < -2 ? 'text-red-600' : 'text-green-600'}`}>
                          {results.difference.diameter > 0 ? '+' : ''}{results.difference.diameter}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Opseg:</span>
                        <span className={`font-semibold ${results.difference.circumference > 2 || results.difference.circumference < -2 ? 'text-red-600' : 'text-green-600'}`}>
                          {results.difference.circumference > 0 ? '+' : ''}{results.difference.circumference}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Brzinomjer:</span>
                        <span className={`font-semibold ${Math.abs(results.difference.speedometer) > 2 ? 'text-red-600' : 'text-green-600'}`}>
                          {results.difference.speedometer > 0 ? '+' : ''}{results.difference.speedometer}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Dimenzije Guma</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator dimenzije guma pomaže vam usporediti različite veličine guma i njihov
                  utjecaj na vozilo. Izračunava važne parametre poput ukupnog promjera, opsega i
                  utjecaja na pokazivanje brzinomjera.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Čitati Oznake na Gumama</h2>
                <p className="text-gray-600 mb-4">
                  Oznaka gume 205/55 R16 znači:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>205 - širina gume u milimetrima</li>
                  <li>55 - profil (visina boka kao postotak širine)</li>
                  <li>R - radijalna konstrukcija</li>
                  <li>16 - promjer naplatka u inčima</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite dimenzije trenutne gume (širina, profil, promjer)</li>
                  <li>Unesite dimenzije nove gume koju želite usporediti</li>
                  <li>Kliknite "Usporedi dimenzije"</li>
                  <li>Pregledajte rezultate i razlike između guma</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Što Rezultati Znače</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Visina boka - stvarna visina gume od naplatka do površine</li>
                  <li>Ukupni promjer - ukupna visina gume s naplatkom</li>
                  <li>Opseg - ukupna duljina vanjskog ruba gume</li>
                  <li>Okretaji/km - broj okretaja gume po kilometru</li>
                  <li>Razlike u postocima pokazuju koliko nova guma odstupa od trenutne</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Razlika u promjeru ne bi trebala prelaziti ±2%</li>
                  <li>Veće razlike mogu utjecati na:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>- Točnost brzinomjera</li>
                      <li>- ABS i sustave kontrole stabilnosti</li>
                      <li>- Potrošnju goriva</li>
                      <li>- Upravljivost vozila</li>
                    </ul>
                  </li>
                  <li>Uvijek provjerite preporuke proizvođača vozila</li>
                  <li>Konzultirajte stručnjaka prije promjene dimenzija guma</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorDimenzijeGuma;